import { Batch } from './batchManager';
import { logger as defaultLogger } from './logging';

/**
 * Returns the configured every-Nth value for progress emissions.
 * Ensures a non-negative integer; defaults to 5 when not provided.
 */
export const getProgressEveryNth = (opts?: { progressEveryNthCompleted?: number }): number => {
	const raw = opts?.progressEveryNthCompleted;
	if (raw == null) return 5;
	if (!Number.isFinite(raw)) return 5;
	const n = Math.floor(raw);
	return n < 0 ? 0 : n;
};

/**
 * Determines if a periodic progress emission should occur for the given counter and step.
 */
export const shouldEmitProgress = (currentCount: number, every: number): boolean => {
	if (every <= 0) return false;
	if (currentCount <= 0) return false;
	return currentCount % every === 0;
};

/**
 * Builds the standard progress payload for onProgress callbacks.
 */
export const buildBatchProgressPayload = (batch: Batch) => ({
	completed: batch.completedCount,
	failed: batch.failedCount,
	skipped: batch.skippedCount,
	cancelled: batch.cancelledCount,
	total: batch.taskIds.length,
});

/**
 * Emits the batch progress callback safely.
 * - When isFinal is true, emits regardless of periodic cadence.
 * - When isFinal is false, emits only if shouldEmitProgress(currentCount, every) is true.
 */
export const safeEmitBatchProgress = async (
	params: {
		batchId: string;
		batch: Batch;
		currentCount: number; // pass batch.completedCount or batch.failedCount depending on context
		isFinal?: boolean;
	},
	log = defaultLogger,
): Promise<void> => {
	const { batchId, batch, currentCount, isFinal = false } = params;
	const every = getProgressEveryNth(batch.options);

	try {
		const shouldEmit = isFinal || shouldEmitProgress(currentCount, every);
		if (shouldEmit && batch.options.onProgress) {
			await batch.options.onProgress(buildBatchProgressPayload(batch));
		}
	} catch (err) {
		log.error(`Error in onProgress callback for batch ${batchId}:`, err);
	}
};

/**
 * Helper to determine if a batch has fully finished (completed + failed == total tasks).
 */
export const isBatchFinished = (batch: Batch): boolean => {
	return (
		batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount === batch.taskIds.length
	);
};
